import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, MapPin, X } from "lucide-react";
import { useLang } from "../App";
import { propertiesI18n } from "../i18n";
import PageHeader from "../components/PageHeader";
import Reveal, { Overline } from "../components/Reveal";
import { SoldRibbon } from "../components/SoldRibbon";
import { resolveImg } from "../images";

const API = process.env.REACT_APP_BACKEND_URL;

export const formatPrice = (n) => `CHF ${Number(n).toLocaleString("de-CH")}.–`;
export const isCommercialRental = (prop) => prop.listing_type === "rental" && prop.category === "commercial";
export const commercialPricingMode = (prop) =>
  prop.commercial_pricing_mode || (Number(prop.rental_price_per_sqm_year) > 0 ? "per_sqm_year" : "monthly_fixed");
export const commercialAnnualRent = (prop) =>
  Number(prop.annual_rent || (Number(prop.rental_price_per_sqm_year) || 0) * (Number(prop.usable_area || prop.area) || 0));
export const rentalGross = (prop) =>
  Number(prop.monthly_gross_rent || (Number(prop.monthly_net_rent) || 0) + (Number(prop.monthly_charges) || 0));
export const formatCharges = (prop, p) =>
  prop.monthly_charges_note ? prop.monthly_charges_note : `${formatPrice(prop.monthly_charges || 0)} ${p.perMonth}`;
export const formatPropertyPrice = (prop, p) => {
  if (isCommercialRental(prop) && commercialPricingMode(prop) === "per_sqm_year" && commercialAnnualRent(prop)) {
    return `${formatPrice(commercialAnnualRent(prop))} ${p.perYear}`;
  }
  return prop.listing_type === "rental" ? `${formatPrice(rentalGross(prop))} ${p.perMonth}` : formatPrice(prop.price);
};

const PRICE_STEPS = [250000, 500000, 750000, 1000000, 1500000, 2000000, 3000000, 5000000];
const RENT_STEPS = [500, 1000, 1500, 2000, 3000, 4000, 5000, 7500];
const ROOM_STEPS = [1.5, 2.5, 3.5, 4.5, 5.5, 6.5];

const DATE_LOCALE = { fr: "fr-CH", de: "de-CH", en: "en-GB" };
export const formatSoldDate = (iso, lang) =>
  new Date(iso).toLocaleDateString(DATE_LOCALE[lang] || "fr-CH", { day: "numeric", month: "long", year: "numeric" });

export const formatAvailability = (prop, p, lang) => {
  const status = prop.availability_status || "available";
  const label = p.availabilityStatus[status] || p.availabilityStatus.available;
  if (status === "from_date" && prop.availability_date) {
    const date = new Date(prop.availability_date);
    const formatted = Number.isNaN(date.getTime())
      ? prop.availability_date
      : date.toLocaleDateString(DATE_LOCALE[lang] || "fr-CH", { day: "numeric", month: "long", year: "numeric" });
    return `${label} ${formatted}`;
  }
  return label;
};

const labelCls = "mb-1 block text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45";

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          className="flen-input w-full cursor-pointer appearance-none pr-6"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-ink/40" />
      </div>
    </div>
  );
}

export default function Properties({ soldOnly = false, rental = false }) {
  const { lang, t } = useLang();
  const p = propertiesI18n[lang];
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [roomsFrom, setRoomsFrom] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    window.scrollTo(0, 0);
    setItems(null);
    fetch(`${API}/api/${rental ? "rentals" : "properties"}`)
      .then((r) => r.json())
      .then((d) => setItems(d.properties))
      .catch(() => setItems([]));
  }, [rental]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (!rental) {
      list = soldOnly ? list.filter((x) => x.sold) : list.filter((x) => !x.sold);
    }
    if (category) list = list.filter((x) => x.category === category);
    if (location) {
      const q = location.toLowerCase();
      list = list.filter((x) => `${x.zip_code} ${x.city}`.toLowerCase().includes(q));
    }
    if (roomsFrom) list = list.filter((x) => x.rooms >= parseFloat(roomsFrom));
    const valueOf = (x) => (rental ? rentalGross(x) : Number(x.price) || 0);
    if (priceFrom) list = list.filter((x) => valueOf(x) >= parseFloat(priceFrom));
    if (priceTo) list = list.filter((x) => valueOf(x) <= parseFloat(priceTo));
    if (sort === "priceAsc") list = [...list].sort((a, b) => valueOf(a) - valueOf(b));
    else if (sort === "priceDesc") list = [...list].sort((a, b) => valueOf(b) - valueOf(a));
    return list;
  }, [items, soldOnly, rental, category, location, roomsFrom, priceFrom, priceTo, sort]);

  const hasFilters = category || location || roomsFrom || priceFrom || priceTo;
  const reset = () => {
    setCategory("");
    setLocation("");
    setRoomsFrom("");
    setPriceFrom("");
    setPriceTo("");
  };

  const priceSteps = rental ? RENT_STEPS : PRICE_STEPS;
  const basePath = rental ? "/rentals" : "/objekte";
  const headline = soldOnly ? p.soldHeadline : rental ? p.rentalHeadline : p.headline;
  const sub = soldOnly ? p.soldSub : rental ? p.rentalSub : p.sub;
  const emptyAll = rental ? p.rentalEmptyAll : p.emptyAll;

  return (
    <div data-testid={rental ? "rentals-page" : "properties-page"} className="min-h-screen bg-ivory text-ink">
      <PageHeader active={rental ? "rentals" : "properties"} />
      <section className="px-6 pb-28 pt-16 md:px-12 md:pt-24 lg:px-20">
        <Overline>{rental ? p.rental : p.pageLabel}</Overline>
        <h1 className="mt-8 font-serif text-4xl font-medium sm:text-5xl lg:text-6xl">{headline}</h1>
        <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-ink/60">{sub}</p>

        <div className="mt-14 border-y border-ink/10 py-8" data-testid="properties-filters">
          <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-3 lg:grid-cols-6">
            <FilterSelect
              label={p.categoryLabel}
              testid="properties-filter-category"
              value={category}
              onChange={setCategory}
              options={[
                { value: "", label: p.allCategories },
                ...Object.entries(p.categories).map(([v, l]) => ({ value: v, label: l })),
              ]}
            />
            <div>
              <span className={labelCls}>{p.locationLabel}</span>
              <input
                data-testid="properties-filter-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={p.locationPlaceholder}
                aria-label={p.locationLabel}
                className="flen-input"
              />
            </div>
            <FilterSelect
              label={p.roomsFrom}
              testid="properties-filter-rooms"
              value={roomsFrom}
              onChange={setRoomsFrom}
              options={[{ value: "", label: "—" }, ...ROOM_STEPS.map((r) => ({ value: String(r), label: String(r) }))]}
            />
            <FilterSelect
              label={rental ? p.rentalPriceFrom : p.priceFrom}
              testid="properties-filter-price-from"
              value={priceFrom}
              onChange={setPriceFrom}
              options={[{ value: "", label: "—" }, ...priceSteps.map((v) => ({ value: String(v), label: formatPrice(v) }))]}
            />
            <FilterSelect
              label={rental ? p.rentalPriceTo : p.priceTo}
              testid="properties-filter-price-to"
              value={priceTo}
              onChange={setPriceTo}
              options={[{ value: "", label: "—" }, ...priceSteps.map((v) => ({ value: String(v), label: formatPrice(v) }))]}
            />
            <FilterSelect
              label={p.sortLabel}
              testid="properties-filter-sort"
              value={sort}
              onChange={setSort}
              options={[
                { value: "newest", label: p.sortNewest },
                { value: "priceAsc", label: p.sortPriceAsc },
                { value: "priceDesc", label: p.sortPriceDesc },
              ]}
            />
          </div>
          {hasFilters && (
            <button
              data-testid="properties-reset"
              onClick={reset}
              className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink/50 transition-colors duration-300 hover:text-bronze"
            >
              <X size={13} /> {p.reset}
            </button>
          )}
        </div>

        <p className="mt-8 text-[11px] uppercase tracking-[0.25em] text-ink/45" data-testid="properties-count">
          {filtered.length === 1 ? p.countOne : p.countMany.replace("{n}", filtered.length)}
        </p>

        {!items ? (
          <p className="mt-16 text-sm text-ink/50">…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="properties-empty" className="mt-20 max-w-md">
            <p className="font-serif text-2xl italic text-ink/60">{hasFilters ? p.empty : soldOnly ? p.emptySold : emptyAll}</p>
          </div>
        ) : (
          <div className="mt-10 grid gap-x-10 gap-y-16 md:grid-cols-2 lg:grid-cols-3" data-testid={rental ? "rentals-grid" : "properties-grid"}>
            {filtered.map((prop, i) => {
              const isRented = rental && prop.availability_status === "rented";
              const surface = isCommercialRental(prop) ? prop.usable_area : prop.area;
              return (
                <Reveal key={prop.id} delay={(i % 3) * 0.08}>
                  <article data-testid={`property-card-${prop.id}`} className="group">
                    <Link to={`${basePath}/${prop.id}`} className="block">
                      <div className="relative aspect-[4/3] overflow-hidden bg-ink/5">
                        {prop.images && prop.images[0] && (
                          <img
                            src={resolveImg(prop.images[0])}
                            alt={prop.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                          />
                        )}
                        {prop.sold || isRented ? (
                          <SoldRibbon label={prop.sold ? p.sold : p.rented} testid={prop.sold ? `property-sold-${prop.id}` : `property-rented-${prop.id}`} />
                        ) : (
                          <span className="absolute left-0 top-0 bg-bronze px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory">
                            {rental ? p.rental : p.sale}
                          </span>
                        )}
                      </div>
                    </Link>
                    <div className="mt-5 flex items-baseline justify-between gap-4">
                      <span className="text-sm font-semibold tracking-wide" data-testid={`property-price-${prop.id}`}>
                        {formatPropertyPrice(prop, p)}
                      </span>
                      <Link
                        to={`${basePath}/${prop.id}`}
                        data-testid={`property-details-link-${prop.id}`}
                        className="group/link flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-bronze"
                      >
                        {p.details}
                        <ArrowRight size={13} className="transition-transform duration-500 group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                    <Link to={`${basePath}/${prop.id}`}>
                      <h2 className="mt-3 font-serif text-xl font-medium leading-snug transition-colors duration-300 group-hover:text-bronze">
                        {lang !== "fr" && prop[`title_${lang}`] ? prop[`title_${lang}`] : prop.title}
                      </h2>
                    </Link>
                    <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.22em] text-ink/50">
                      {p.categories[prop.category]}
                    </p>
                    {prop.ref && (
                      <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ink/35">{prop.ref}</p>
                    )}
                    <p className="mt-1.5 text-xs font-light text-ink/55">
                      {prop.rooms > 0 ? `${prop.rooms} ${p.roomsUnit}` : ""}
                      {prop.rooms > 0 && surface > 0 ? " · " : ""}
                      {surface > 0 ? `${surface} ${p.areaUnit}` : ""}
                    </p>
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs font-light text-ink/45">
                      <MapPin size={12} /> {prop.zip_code} {prop.city}
                    </p>
                    {isCommercialRental(prop) && commercialPricingMode(prop) === "per_sqm_year" && Number(prop.rental_price_per_sqm_year) > 0 && (
                      <p data-testid={`property-rental-rate-${prop.id}`} className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink/50">
                        {formatPrice(prop.rental_price_per_sqm_year)} {p.perSqmYear}
                      </p>
                    )}
                    {rental && (
                      <p data-testid={`property-availability-${prop.id}`} className="mt-2.5 border-l-2 border-bronze/60 pl-3 text-[11px] font-medium uppercase tracking-[0.18em] text-bronze">
                        {formatAvailability(prop, p, lang)}
                      </p>
                    )}
                    {prop.sold && prop.sold_at && (
                      <p data-testid={`property-sold-date-${prop.id}`} className="mt-2.5 border-l-2 border-bronze/60 pl-3 text-[11px] font-medium uppercase tracking-[0.18em] text-bronze">
                        {p.soldOn} {formatSoldDate(prop.sold_at, lang)}
                      </p>
                    )}
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>
      <div className="border-t border-ink/10 px-6 py-6 text-[12px] font-light text-ink/40 md:px-12 lg:px-20">
        {t.footer.rights}
      </div>
    </div>
  );
}
